import React from "react";

/**
 * Typographic Concrete Control wordmark.
 * The first "o" in "C[o]ncrete" is rendered as a teal ring SVG that echoes
 * a thermal contour — same treatment as the original brand mark.
 * - variant: "light" (default, dark text) or "dark" (light text on dark surface)
 * - size: tailwind text size class for the wordmark (e.g. "text-base", "text-lg")
 *
 * A11y: the SVG ring breaks innerText extraction into "C" + "ncrete Control".
 * A visually-hidden `<span className="sr-only">Concrete Control</span>` is
 * rendered inside the wrapper so screen readers and text-extraction tools
 * see the contiguous brand string. Combined with the existing aria-label,
 * the rendered DOM exposes the full "Concrete Control" string.
 */
export const Wordmark = ({
    variant = "light",
    size = "text-base",
    className = "",
}) => {
    const textColor = variant === "dark" ? "text-[#e6ebec]" : "text-[#0e1416]";

    const Ring = () => (
        <span
            aria-hidden="true"
            className="relative inline-block translate-y-[1px]"
            style={{ width: "0.62em", height: "0.62em" }}
        >
            <svg viewBox="0 0 20 20" width="100%" height="100%" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="7.5" stroke="#01696f" strokeWidth="2.6" />
                <circle cx="10" cy="10" r="2.4" fill="#01696f" />
            </svg>
        </span>
    );

    return (
        <span
            data-testid="concrete-control-wordmark"
            className={`inline-flex items-baseline gap-[1px] font-medium tracking-tight ${textColor} ${size} ${className}`}
            aria-label="Concrete Control"
        >
            {/* Visually-hidden full brand string so innerText / screen readers
                see the contiguous "Concrete Control" even though the visual
                "o" is an SVG ring. */}
            <span className="sr-only">Concrete Control</span>
            <span aria-hidden="true">C</span>
            <Ring />
            <span aria-hidden="true">ncrete&nbsp;Control</span>
        </span>
    );
};

export default Wordmark;
